import { useCallback, useEffect, useState } from 'react';
import { companiesApi, hrApi } from '@/api';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormActions } from '@/components/ui/FormActions';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { HrHolidayType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

type Props = {
  companyId?: number;
  compact?: boolean;
  className?: string;
  onTypesChange?: (types: HrHolidayType[]) => void;
  /** Keep compact inline buttons inside modals. */
  inlineActions?: boolean;
};

export function HrHolidayTypesSection({ companyId, compact, className, onTypesChange, inlineActions = false }: Props) {
  const [types, setTypes] = useState<HrHolidayType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nonWorkingDay, setNonWorkingDay] = useState('true');
  const [sortOrder, setSortOrder] = useState('0');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = companyId
        ? await companiesApi.listHolidayTypes(companyId)
        : await hrApi.listHolidayTypes(true);
      setTypes(rows);
      onTypesChange?.(rows.filter((t) => t.active));
    } catch (err) {
      showApiError(err, 'Failed to load holiday types');
    } finally {
      setLoading(false);
    }
  }, [companyId, onTypesChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setEditId(null);
    setCode('');
    setName('');
    setDescription('');
    setNonWorkingDay('true');
    setSortOrder('0');
  };

  const startEdit = (row: HrHolidayType) => {
    setEditId(row.id);
    setCode(row.code);
    setName(row.name);
    setDescription(row.description ?? '');
    setNonWorkingDay(row.nonWorkingDay ? 'true' : 'false');
    setSortOrder(String(row.sortOrder ?? 0));
  };

  const save = async () => {
    if (!code.trim() || !name.trim()) {
      notify.error('Code and name are required');
      return;
    }
    const body = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      description: description.trim() || undefined,
      nonWorkingDay: nonWorkingDay === 'true',
      sortOrder: Number(sortOrder) || 0,
      active: true,
    };
    setSaving(true);
    try {
      if (editId != null) {
        if (companyId) await companiesApi.updateHolidayType(companyId, editId, body);
        else await hrApi.updateHolidayType(editId, body);
        notify.success('Holiday type updated');
      } else {
        if (companyId) await companiesApi.createHolidayType(companyId, body);
        else await hrApi.createHolidayType(body);
        notify.success('Holiday type created');
      }
      resetForm();
      await load();
    } catch (err) {
      showApiError(err, editId != null ? 'Update failed' : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: number) => {
    try {
      if (companyId) await companiesApi.deactivateHolidayType(companyId, id);
      else await hrApi.deactivateHolidayType(id);
      notify.success('Holiday type deactivated');
      if (editId === id) resetForm();
      await load();
    } catch (err) {
      showApiError(err, 'Deactivate failed');
    }
  };

  const columns: Column<HrHolidayType>[] = [
      { key: 'code', header: 'Code' },
      { key: 'name', header: 'Name' },
      {
        key: 'nonWorkingDay',
        header: 'Non-working',
        render: (r) => (r.nonWorkingDay ? 'Yes' : 'No'),
      },
      { key: 'sortOrder', header: 'Order' },
      {
        key: 'active',
        header: 'Status',
        render: (r) => (
          <span
            className={cn(
              'rounded px-2 py-0.5 text-xs font-medium',
              r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600',
            )}
          >
            {r.active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        actions: true,
        render: (r) => (
          <PageActionGate action="edit">
            <TableRowActions>
              <Button variant="secondary" size="sm" onClick={() => startEdit(r)}>
                Edit
              </Button>
              {r.active && (
                <Button variant="ghost" size="sm" onClick={() => void deactivate(r.id)}>
                  Deactivate
                </Button>
              )}
            </TableRowActions>
          </PageActionGate>
        ),
      },
  ];

  return (
    <div className={cn('space-y-4', className)}>
      {!compact && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Define the holiday categories your company uses (e.g. public, regional, optional). When adding a holiday on the
          calendar tab, pick one of these types. Deactivate types you do not need instead of deleting them.
        </p>
      )}

      <PageActionGate action="create">
        <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <h3 className="mb-3 text-sm font-medium">{editId != null ? 'Edit holiday type' : 'Add holiday type'}</h3>
          <FormGrid columns={compact ? 2 : 3} className="items-end gap-3">
            <Input
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. REGIONAL"
            />
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" />
            <Input
              label="Sort order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
            <Input
              label="Description"
              className={compact ? 'col-span-2' : 'col-span-3'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional note for HR admins"
            />
            <Select
              label="Counts as non-working day"
              value={nonWorkingDay}
              onChange={setNonWorkingDay}
              options={[
                { value: 'true', label: 'Yes — blocks attendance / leave calendar' },
                { value: 'false', label: 'No — informational only' },
              ]}
            />
          </FormGrid>
          {inlineActions ? (
            <div className="mt-4 flex justify-end gap-2">
              {editId != null && (
                <Button variant="secondary" onClick={resetForm}>
                  Cancel
                </Button>
              )}
              <Button loading={saving} onClick={() => void save()}>
                {editId != null ? 'Save' : 'Add type'}
              </Button>
            </div>
          ) : (
            <FormActions
              primaryLabel={editId != null ? 'Save' : 'Add type'}
              primaryLoading={saving}
              onPrimary={() => void save()}
              onCancel={editId != null ? resetForm : undefined}
              onReset={editId == null ? resetForm : undefined}
            />
          )}
        </div>
      </PageActionGate>

      <DataTable
        columns={columns}
        data={types}
        loading={loading}
        keyField="id"
        emptyTitle="No holiday types"
        emptyDescription="Add types your company uses, then assign them when creating holidays."
      />
    </div>
  );
}
